import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {useNavigate} from 'react-router';
import {useReportStore} from '../../../../../../features/products/useReportStore';
import {updateReportDoc} from '../../../../../../firebase/firestore/reports';
import {
    scheduleExpertProfilePipelineAfterFintelApproval,
    triggerExpertProfileUpdate,
} from '../../../../../../features/experts/expertProfileService';
import {getValidationStepsForProductType, isCreateIcrStep} from '../../constants/validationStepSelectors';
import {loadPitchTextFromReport} from '../../../../../../utils/exportReportHelper';
import {generateIcrDraftFromPitch} from '../../../../../../utils/api/icrGeneration';
import NewIcrPage from '../../../../../../features/collection-requests/NewIcrPage';

const EMPTY_OBJECT = Object.freeze({});

const ValidationForm = ({stepIndex = 0, productTypeValue = null}) => {
    const navigate = useNavigate();
    const reportId = useReportStore(s => s.id || s.reportId);
    const reportTitle = useReportStore(s => s.title || '');
    const reportMeta = useReportStore(s => s.meta);
    const reportProductType = useReportStore(s => s.productType);
    const reportContent = useReportStore(s => s.content || null);
    const existingValidation = useReportStore(s => s.validation);
    const stepValidation = useReportStore(s => s.validation?.[stepIndex]);
    const updateReportStore = useReportStore(s => s.updateReport);
    const reportStatus = useReportStore(s => s.status);
    const snapshotPath = useReportStore(s => s.snapshotPath);
    const safeReportMeta = reportMeta ?? EMPTY_OBJECT;
    const safeReportProductType = reportProductType ?? EMPTY_OBJECT;
    const safeExistingValidation = existingValidation ?? EMPTY_OBJECT;

    // Use appropriate validation steps based on product type
    const validationSteps = getValidationStepsForProductType(productTypeValue);
    const step = useMemo(() => validationSteps[stepIndex] || validationSteps[0], [stepIndex, validationSteps]);
    const createIcrStep = isCreateIcrStep(step);

    const initial = stepValidation || {passed: undefined, feedback: ''};
    const [feedback, setFeedback] = useState(initial.feedback || '');
    const [saving, setSaving] = useState(false);
    const [generatingIcr, setGeneratingIcr] = useState(false);
    const [isCreateIcrModalOpen, setIsCreateIcrModalOpen] = useState(false);
    const [icrDraftPrefill, setIcrDraftPrefill] = useState(null);
    const [icrLlmBaseline, setIcrLlmBaseline] = useState(null);
    const [snack, setSnack] = useState({open: false, severity: 'success', msg: ''});
    const [isEditing, setIsEditing] = useState(false);

    const existingIcrNumber = useMemo(() => {
        const maybeNumber = safeReportMeta?.icrNumber;
        if (maybeNumber === undefined || maybeNumber === null) return '';
        return String(maybeNumber).trim();
    }, [safeReportMeta]);

    const savedIcrDraft = stepValidation?.draftForm;
    const hasSavedIcrDraft = savedIcrDraft && typeof savedIcrDraft === 'object'
        && Object.keys(savedIcrDraft).length > 0;

    const icrStepComplete = stepValidation?.passed === true || Boolean(existingIcrNumber);

    // Heal step 4 when an ICR is linked but validation was not persisted (e.g. prior 403)
    useEffect(() => {
        if (!createIcrStep || !reportId || !existingIcrNumber || stepValidation?.passed === true) return;

        let cancelled = false;
        const heal = async () => {
            const nextValidation = {
                ...safeExistingValidation,
                [stepIndex]: {
                    passed: true,
                    feedback: `ICR linked: ${existingIcrNumber}`,
                },
            };
            try {
                await updateReportDoc(reportId, {validation: nextValidation});
                if (!cancelled) {
                    updateReportStore({validation: nextValidation});
                }
            } catch (err) {
                console.warn('Failed to heal ICR step validation', err);
            }
        };

        heal();
        return () => {
            cancelled = true;
        };
    }, [
        createIcrStep,
        reportId,
        existingIcrNumber,
        stepValidation?.passed,
        stepIndex,
        safeExistingValidation,
        updateReportStore,
    ]);

    // Sync local feedback with the store's value for the current step
    useEffect(() => {
        setFeedback(stepValidation?.feedback || '');
        // Reset editing mode when user switches steps so saved view shows by default
        setIsEditing(false);
    }, [stepValidation, stepIndex]);

    const disabled = !reportId || saving || generatingIcr;

    const persist = async (passedValue) => {
        if (!reportId) return;

        if (createIcrStep && passedValue === true && !existingIcrNumber) {
            setSnack({open: true, severity: 'error', msg: 'Create and link an ICR first'});
            return;
        }

        try {
            setSaving(true);

            const nextValidation = {
                ...safeExistingValidation,
                [stepIndex]: {passed: passedValue, feedback: feedback?.trim?.() || ''},
            };

            // Check if all required steps are approved (1 for disclosure, 3 for report)
            const allApproved = validationSteps.every((_, idx) => {
                const stepData = idx === stepIndex ? nextValidation[idx] : safeExistingValidation[idx];
                return stepData && stepData.passed === true;
            });

            // Build payload: always persist merged validation, and set status to Draft on Reject only
            const updatePayload = {validation: nextValidation};
            if (passedValue === false) {
                updatePayload.status = 'Draft';
            } else if (allApproved) {
                // All required steps are approved => change status to "Approved"
                updatePayload.status = 'approved';
            }

            const wasApprovedFintel = reportStatus === 'approved';

            // Persist the full merged validation object so we don't accidentally overwrite other steps
            await updateReportDoc(reportId, updatePayload);

            if (allApproved) {
                scheduleExpertProfilePipelineAfterFintelApproval(reportId, snapshotPath);
            } else if (passedValue === false && wasApprovedFintel) {
                // Rejection rolled an approved FINTEL back to Draft; regenerate profiles from remaining approved products
                triggerExpertProfileUpdate(reportId).catch((err) =>
                    console.error('[ValidationForm] Expert profile update after validation reject failed:', err)
                );
            }

            // Update local store to reflect changes immediately (mirror status change on reject)
            const storeUpdate = {validation: nextValidation};
            if (passedValue === false) {
                storeUpdate.status = 'Draft';
            } else if (allApproved) {
                storeUpdate.status = 'approved';
            }
            updateReportStore(storeUpdate);

            // exit edit mode after successful save so user sees the saved summary
            setIsEditing(false);

            if (allApproved) {
                setSnack({open: true, severity: 'success', msg: 'Report approved! Returning to review list…'});
                setTimeout(() => navigate('/products'), 2500);
            } else if (passedValue === false) {
                setSnack({open: true, severity: 'info', msg: 'Report rejected and returned to draft'});
                setTimeout(() => navigate('/products'), 2500);
            } else {
                setSnack({open: true, severity: 'success', msg: 'Validation saved'});
            }
        } catch (e) {
            console.error('Failed to save validation', e);
            setSnack({open: true, severity: 'error', msg: 'Failed to save validation'});
        } finally {
            setSaving(false);
        }
    };

    const openPrefilledIcrForm = async () => {
        if (!reportId || generatingIcr) return;
        if (existingIcrNumber) {
            setSnack({open: true, severity: 'success', msg: `ICR already linked: ${existingIcrNumber}`});
            return;
        }

        if (hasSavedIcrDraft) {
            setIcrDraftPrefill(savedIcrDraft);
            setIcrLlmBaseline(stepValidation?.llmBaseline || savedIcrDraft);
            setIsCreateIcrModalOpen(true);
            return;
        }

        setGeneratingIcr(true);
        try {
            const pitchText = await loadPitchTextFromReport(
                reportId,
                snapshotPath,
                reportContent,
            );

            if (!pitchText) {
                throw new Error('Pitch content is empty. Add report content before creating an ICR.');
            }

            const metadataForPrompt = {
                ...safeReportMeta,
                productTypeValue: safeReportProductType?.value || productTypeValue,
                productType: safeReportProductType?.label || safeReportProductType?.value || productTypeValue,
            };

            const draft = await generateIcrDraftFromPitch({
                reportId,
                pitchText,
                metadata: metadataForPrompt,
                title: reportTitle,
            });

            setIcrDraftPrefill(draft);
            setIcrLlmBaseline(draft);
            setIsCreateIcrModalOpen(true);
        } catch (error) {
            console.error('Failed to generate ICR draft from pitch', error);
            setSnack({open: true, severity: 'error', msg: error.message || 'Failed to generate ICR draft'});
        } finally {
            setGeneratingIcr(false);
        }
    };

    // Prepare display condition for current step's saved data
    const hasCurrentSaved = stepValidation && (stepValidation.feedback || stepValidation.passed !== undefined);

    // Decide whether to show the editable form: edit mode OR no saved data
    const showForm = isEditing || !hasCurrentSaved;

    const handleIcrModalClose = () => {
        setIsCreateIcrModalOpen(false);
        setIcrDraftPrefill(null);
        setIcrLlmBaseline(null);
    };

    const handleDraftSaved = ({validation}) => {
        setIsCreateIcrModalOpen(false);
        setIcrDraftPrefill(null);
        setIcrLlmBaseline(null);
        if (validation) {
            updateReportStore({validation});
        }
        setSnack({open: true, severity: 'success', msg: 'ICR draft saved'});
    };

    const handleIcrFormError = (error) => {
        setSnack({
            open: true,
            severity: 'error',
            msg: error?.message || 'Failed to save ICR form',
        });
    };

    const handleIcrCreated = async ({createdIcr, reportUpdatePayload}) => {
        setIsCreateIcrModalOpen(false);
        setIcrDraftPrefill(null);

        if (reportUpdatePayload) {
            const nextStoreUpdate = {
                meta: {
                    ...safeReportMeta,
                    ...(reportUpdatePayload.meta || {}),
                },
                validation: reportUpdatePayload.validation || safeExistingValidation,
            };

            if (reportUpdatePayload.status) {
                nextStoreUpdate.status = reportUpdatePayload.status;
            }

            updateReportStore(nextStoreUpdate);
        }

        setSnack({open: true, severity: 'success', msg: `ICR created: ${createdIcr?.icrNumber || ''}`});
    };

    return (
        <Paper variant="outlined" sx={{p: 2}}>
            <Stack spacing={2}>
                <Stack spacing={0.5}>
                    <Typography variant="h6">{step.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{step.instruction}</Typography>
                </Stack>

                {createIcrStep ? (
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            This step is required for pitch reviews. Generate a prefilled ICR form, review it, then
                            create the ICR.
                        </Typography>
                        {existingIcrNumber ? (
                            <Stack spacing={1.5}>
                                <Typography variant="body1">
                                    Linked ICR: <strong>{existingIcrNumber}</strong>
                                </Typography>
                                {icrStepComplete ? (
                                    <Typography variant="body2" color="success.main">
                                        Step complete — ICR created and linked.
                                    </Typography>
                                ) : null}
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate(`/icr/${encodeURIComponent(existingIcrNumber)}`)}
                                    disabled={disabled}
                                >
                                    Open ICR
                                </Button>
                            </Stack>
                        ) : (
                            <Stack spacing={1.5}>
                                <Button
                                    variant="contained"
                                    onClick={openPrefilledIcrForm}
                                    disabled={disabled || generatingIcr}
                                >
                                    {generatingIcr
                                        ? 'Generating ICR Draft...'
                                        : (hasSavedIcrDraft ? 'Continue ICR Draft' : 'Generate and Open ICR Form')}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                ) : null}

                {!createIcrStep && showForm ? (
                    <>
                        <TextField
                            label="Feedback"
                            placeholder="Type your feedback here, then click Approve / Reject"
                            multiline
                            minRows={6}
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            disabled={disabled}
                            fullWidth
                        />

                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" color="success" disabled={disabled || !(feedback || '').trim()}
                                    onClick={() => persist(true)}>
                                Approve
                            </Button>
                            <Button variant="outlined" color="error" disabled={disabled || !(feedback || '').trim()}
                                    onClick={() => persist(false)}>
                                Reject
                            </Button>
                        </Stack>
                    </>
                ) : !createIcrStep ? (
                    <Stack p={2} spacing={2} bgcolor={'background.paper'} alignItems={'flex-start'} borderRadius={1}
                           border={'1px solid #cde'}>
                        <Stack spacing={1}>
                            <Typography variant="body1" sx={{fontWeight: 600}}>
                                {stepValidation.passed === true ? 'Passed' : stepValidation.passed === false ? 'Not pass' : 'Pending'}
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary" sx={{mb: 0.5}}>Feedback:</Typography>
                            <Typography variant="body1" sx={{whiteSpace: 'pre-wrap', mt: 0.5}}>
                                {stepValidation.feedback || '(no feedback)'}
                            </Typography>
                        </Stack>
                        <Button size="small" variant="outlined" onClick={() => setIsEditing(true)}>Edit</Button>
                    </Stack>
                ) : null}
            </Stack>


            <Snackbar
                open={snack.open}
                autoHideDuration={2500}
                onClose={() => setSnack(prev => ({...prev, open: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert severity={snack.severity} variant="filled" sx={{width: '100%'}}>
                    {snack.msg}
                </Alert>
            </Snackbar>

            <Dialog
                open={isCreateIcrModalOpen}
                onClose={() => generatingIcr ? null : handleIcrModalClose()}
                scroll="body"
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Create ICR</DialogTitle>
                <DialogContent>
                    <NewIcrPage
                        embedded
                        prefill={icrDraftPrefill}
                        llmBaseline={icrLlmBaseline}
                        sourceContext={{
                            sourceReportId: reportId,
                            sourceReportTitle: reportTitle,
                            sourceReportMeta: safeReportMeta,
                            sourceProductTypeValue: safeReportProductType?.value || productTypeValue,
                            validationStepIndex: stepIndex,
                            sourceValidation: safeExistingValidation,
                            sourceSnapshotPath: snapshotPath,
                        }}
                        onCreated={handleIcrCreated}
                        onDraftSaved={handleDraftSaved}
                        onError={handleIcrFormError}
                        onCancel={handleIcrModalClose}
                    />
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default ValidationForm;
